import useBreadcrumbs from "use-react-router-breadcrumbs";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/react";
import { Link, RouteComponentProps } from "react-router-dom";

import { useContextConsumer } from "modules/context/Providers/ContextProvider";
import { useIsUserLogged } from "modules/authentication/infrastructure/useIsUserLogged";

import { useGetFriendlyUrl } from "utils/hooks/useGetFriendlyUrl";

import { ChakraText } from "components/ChakraText";

import { GroupBreadcrumb } from "./components/GroupBreadcrumb";
import { QueueBreadcrumb } from "./components/QueueBreadcrumb";
import styled from "@emotion/styled";

const Breadcrumbs = () => {
  const isUserLogged = useIsUserLogged();
  const { friendlyUrl } = useGetFriendlyUrl();
  const breadcrumbRoutes = [
    ...(isUserLogged ? [...protectedRoutes] : []),
    ...routes,
  ];
  const breadcrumbs = useBreadcrumbs(breadcrumbRoutes, {
    disableDefaults: true,
  });
  const { siteTitle } = useContextConsumer();

  return (
    <BreadcrumbsContainer>
      <Breadcrumb>
        <BreadcrumbItem id="main-subject-breadcrumb">
          <BreadcrumbLink
            as="a"
            href={`${window.location.origin}/${friendlyUrl}`}
          >
            {siteTitle}
          </BreadcrumbLink>
        </BreadcrumbItem>
        {breadcrumbs.map(({ breadcrumb, match }, index) => {
          const isCurrent = breadcrumbs.length - 1 === index;
          return (
            <BreadcrumbItem
              key={index}
              isCurrentPage={isCurrent}
              id={`breadcrumb-${index + 1}`}
            >
              {isCurrent ? (
                <ChakraText fontWeight="bold">{breadcrumb}</ChakraText>
              ) : (
                <BreadcrumbLink as={Link} to={match.url}>
                  {breadcrumb}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>
    </BreadcrumbsContainer>
  );
};

const BreadcrumbsContainer = styled.div`
  ol {
    padding-left: 5px;
  }
`;

const protectedRoutes = [
  { path: "/e-rezerwacja/:friendlyUrl/uzytkownik", breadcrumb: null },
  {
    path: "/e-rezerwacja/:friendlyUrl/uzytkownik/profil",
    breadcrumb: () => <ChakraText>Profil użytkownika</ChakraText>,
  },
  {
    path: "/e-rezerwacja/:friendlyUrl/uzytkownik/aktualne-wizyty",
    breadcrumb: () => <ChakraText>Aktualne wizyty</ChakraText>,
  },
  {
    path: "/e-rezerwacja/:friendlyUrl/uzytkownik/historyczne-wizyty",
    breadcrumb: () => <ChakraText>Historyczne wizyty</ChakraText>,
  },
  {
    path:
      "/e-rezerwacja/:friendlyUrl/uzytkownik/historyczne-wizyty/:appointmentId",
    breadcrumb: () => <ChakraText>Wizyta</ChakraText>,
  },
  {
    path:
      "/e-rezerwacja/:friendlyUrl/uzytkownik/aktualne-wizyty/:appointmentId",
    breadcrumb: () => <ChakraText>Wizyta</ChakraText>,
  },
];

const routes = [
  { path: "/e-rezerwacja/:friendlyUrl/potwierdzenie", breadcrumb: null },
  { path: "/e-rezerwacja/:friendlyUrl/edycja", breadcrumb: null },
  { path: "/e-rezerwacja/:friendlyUrl/anulowanie", breadcrumb: null },
  { path: "/e-rezerwacja/:friendlyUrl/weryfikacja", breadcrumb: null },
  { path: "/e-rezerwacja/:friendlyUrl/powrot-sso", breadcrumb: null },
  { path: "/e-rezerwacja/:friendlyUrl/zmien-haslo", breadcrumb: null },
  {
    path: "/e-rezerwacja/:friendlyUrl/aktualne-wizyty/:appointmentId",
    breadcrumb: () => <ChakraText>Szczegóły wizyty</ChakraText>,
  },
  {
    path: "/e-rezerwacja/:friendlyUrl/:groupId",
    breadcrumb: (
      routeProps: RouteComponentProps<{ groupId: string; friendlyUrl: string }>
    ) => {
      const { friendlyUrl, groupId } = routeProps.match.params;
      return <GroupBreadcrumb groupId={groupId} friendlyUrl={friendlyUrl} />;
    },
  },
  {
    path: "/e-rezerwacja/:friendlyUrl/potwierdzenie/:tokenId",
    breadcrumb: () => <ChakraText>Potwierdzenie terminu wizyty</ChakraText>,
  },
  {
    path: "/e-rezerwacja/:friendlyUrl/weryfikacja/:tokenId",
    breadcrumb: () => <ChakraText>Potwierdzenie rejestracji</ChakraText>,
  },
  {
    path: "/e-rezerwacja/:friendlyUrl/zmien-haslo/:tokenId",
    breadcrumb: () => <ChakraText>Reset hasła</ChakraText>,
  },
  {
    path: "/e-rezerwacja/:friendlyUrl/edycja/:tokenId",
    breadcrumb: () => <ChakraText>Zmiana terminu wizyty</ChakraText>,
  },
  {
    path: "/e-rezerwacja/:friendlyUrl/anulowanie/:tokenId",
    breadcrumb: () => <ChakraText>Anulowanie terminu wizyty</ChakraText>,
  },
  {
    path: "/e-rezerwacja/:friendlyUrl/:groupId/rezerwacja/:queueId",
    breadcrumb: (
      routeProps: RouteComponentProps<{ queueId: string; friendlyUrl: string }>
    ) => {
      const { friendlyUrl, queueId } = routeProps.match.params;
      return <QueueBreadcrumb queueId={queueId} friendlyUrl={friendlyUrl} />;
    },
  },
  {
    path:
      "/e-rezerwacja/:friendlyUrl/:groupId/rezerwacja/:queueId/podsumowanie/:appointmentId",
    breadcrumb: () => <ChakraText>Podsumowanie</ChakraText>,
  },
  {
    path:
      "/e-rezerwacja/:friendlyUrl/:groupId/rezerwacja/:queueId/edycja/:appointmentId",
    breadcrumb: () => <ChakraText>Edycja</ChakraText>,
  },
  {
    path: "/e-rezerwacja/:friendlyUrl/powrot-sso/:ssoToken",
    breadcrumb: () => <ChakraText>Logowanie SSO</ChakraText>,
  },
];

export { Breadcrumbs };
